import { ReactNode } from "react";
import { Checkbox } from "@/app/components/shadcn/checkbox";
import { Star, ChevronRight, Truck } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { getSoftBadgeColors } from "@/app/utils/badgeColors";
import { trackInteraction, trackCampaignClick } from "@/app/lib/tracking/interactions";

interface CartBanner {
  type: string;
  text: string;
  color: string;
  color_hex: string;
  icon: string;
  icon_full: string;
}

interface FreeShippingInfo {
  is_free: boolean;
  background_color_hex?: string;
  text_color_hex?: string;
  icon?: string;
  remaining: string;
  remaining_raw: number;
  remaining_text: string;
  progress: number;
}

interface SellerCampaign {
  campaign_id: number;
  title: string;
  icon_url: string;
  is_campaign_implemented: boolean;
  detail_text: string;
  backgroud_color: string;
  text_color_hex: string;
  slug: string;
}

interface SellerCardProps {
  sellerId: number;
  name: string;
  rating: number;
  sellerUrl: string;
  children: ReactNode;
  banners?: CartBanner[];
  discountBanner?: ReactNode;
  freeShippingInfo?: FreeShippingInfo;
  campaigns?: SellerCampaign[];
  allSelected?: boolean;
  onToggleAll?: () => void;
  isTogglingAll?: boolean;
}

const SellerCard = ({
  sellerId,
  name,
  rating,
  sellerUrl,
  children,
  banners,
  discountBanner,
  freeShippingInfo,
  campaigns,
  allSelected = false,
  onToggleAll,
  isTogglingAll = false,
}: SellerCardProps) => {
  return (
    <div className="bg-white rounded-lg lg:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-3 lg:p-4 border-b border-gray-100">
        <div className="flex items-center gap-2 lg:gap-3">
          <Checkbox
            checked={allSelected}
            onCheckedChange={onToggleAll}
            loading={isTogglingAll}
            className="size-4 lg:size-5"
          />
          <Link
            href={sellerUrl}
            onClick={() => trackInteraction("seller", sellerId, "click")}
            className="flex items-center gap-2 lg:gap-3 hover:text-blue-600 transition-colors flex-1 min-w-0"
          >
            <div className="flex items-center gap-1 lg:gap-2 min-w-0">
              <span className="text-xs lg:text-sm text-gray-600 shrink-0">
                Satıcı:
              </span>
              <span className="font-semibold text-gray-900 text-xs lg:text-sm truncate">
                {name}
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <div className="size-5 lg:size-6 bg-green-500 rounded-md flex items-center justify-center text-white font-medium text-[10px] lg:text-xs">
                {rating}
              </div>
              <Star className="w-2.5 h-2.5 lg:w-3 lg:h-3 fill-yellow-400 text-yellow-400" />
            </div>
            <ChevronRight className="w-3 h-3 lg:w-4 lg:h-4 shrink-0" />
          </Link>

          {campaigns && campaigns.length > 0 && (
            <div className="hidden lg:flex items-center gap-2 shrink-0 ml-auto">
              {campaigns.map((campaign) => {
                const colors = getSoftBadgeColors(campaign.backgroud_color);
                return (
                  <Link
                    key={campaign.campaign_id}
                    href={`/kampanya/${campaign.slug}`}
                    onClick={() => trackCampaignClick(campaign.campaign_id)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: colors.bg }}
                  >
                    {campaign.icon_url && (
                      <div className="relative w-4 h-4 shrink-0">
                        <Image src={campaign.icon_url} alt={campaign.title} fill className="object-contain" />
                      </div>
                    )}
                    <span className="text-xs font-medium whitespace-nowrap" style={{ color: colors.text }}>
                      {campaign.title}
                    </span>
                    <ChevronRight className="w-3 h-3" style={{ color: colors.text }} />
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {campaigns && campaigns.length > 0 && (
          <div className="lg:hidden mt-2 flex flex-wrap gap-2">
            {campaigns.map((campaign) => {
              const colors = getSoftBadgeColors(campaign.backgroud_color);
              return (
                <Link
                  key={campaign.campaign_id}
                  href={`/kampanya/${campaign.slug}`}
                  onClick={() => trackCampaignClick(campaign.campaign_id)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-md cursor-pointer hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: colors.bg }}
                >
                  {campaign.icon_url && (
                    <div className="relative w-3.5 h-3.5 shrink-0">
                      <Image src={campaign.icon_url} alt={campaign.title} fill className="object-contain" />
                    </div>
                  )}
                  <span className="text-[10px] font-medium whitespace-nowrap" style={{ color: colors.text }}>
                    {campaign.title}
                  </span>
                  <ChevronRight className="w-2.5 h-2.5" style={{ color: colors.text }} />
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {freeShippingInfo && !freeShippingInfo.is_free && (
        <div
          className="p-3 lg:p-4 border-b"
          style={{
            backgroundColor: freeShippingInfo.background_color_hex
              ? getSoftBadgeColors(freeShippingInfo.background_color_hex).bg
              : "#EFF6FF",
          }}
        >
          <div className="flex items-start gap-2 lg:gap-3">
            {freeShippingInfo.icon ? (
              <div className="relative w-4 h-4 lg:w-5 lg:h-5 shrink-0 mt-0.5">
                <Image
                  src={freeShippingInfo.icon}
                  alt="Shipping"
                  fill
                  className="object-contain"
                />
              </div>
            ) : (
              <Truck
                className="w-4 h-4 lg:w-5 lg:h-5 shrink-0 mt-0.5"
                style={{
                  color: freeShippingInfo.text_color_hex || "#2563EB",
                }}
              />
            )}
            <div className="flex-1 min-w-0">
              <p
                className="text-xs lg:text-sm font-medium"
                style={{
                  color: freeShippingInfo.text_color_hex || "#1D4ED8",
                }}
              >
                {freeShippingInfo.remaining_text}
              </p>
            </div>
          </div>
        </div>
      )}

      {banners &&
        banners.map((banner, index) => {
          const colors = getSoftBadgeColors(banner.color_hex);

          return (
            <div
              key={index}
              className="p-3 lg:p-4 border-b"
              style={{
                backgroundColor: colors.bg,
              }}
            >
              <div className="flex items-center gap-2 lg:gap-3">
                {banner.icon_full && (
                  <div className="relative w-4 h-4 lg:w-5 lg:h-5 shrink-0">
                    <Image
                      src={banner.icon_full}
                      alt={banner.type}
                      fill
                      className="object-contain"
                    />
                  </div>
                )}
                <p
                  className="text-xs lg:text-sm font-medium"
                  style={{ color: colors.text }}
                >
                  {banner.text}
                </p>
              </div>
            </div>
          );
        })}

      {discountBanner}

      {freeShippingInfo && freeShippingInfo.is_free && (
        <div
          className="p-3 lg:p-4 border-b"
          style={{
            backgroundColor: freeShippingInfo.background_color_hex
              ? getSoftBadgeColors(freeShippingInfo.background_color_hex).bg
              : "#ECFDF5",
          }}
        >
          <div className="flex items-center gap-2 lg:gap-3">
            {freeShippingInfo.icon ? (
              <div className="relative w-4 h-4 lg:w-5 lg:h-5 shrink-0">
                <Image
                  src={freeShippingInfo.icon}
                  alt="Free Shipping"
                  fill
                  className="object-contain"
                />
              </div>
            ) : (
              <Truck
                className="w-4 h-4 lg:w-5 lg:h-5 shrink-0"
                style={{
                  color: freeShippingInfo.text_color_hex || "#059669",
                }}
              />
            )}
            <p
              className="text-xs lg:text-sm font-medium"
              style={{
                color: freeShippingInfo.text_color_hex || "#047857",
              }}
            >
              {freeShippingInfo.remaining_text}
            </p>
          </div>
        </div>
      )}

      <div className="p-3 lg:p-4 space-y-3">{children}</div>
    </div>
  );
};

export default SellerCard;
